import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';

import { AuthProvider, useAuth } from './AuthContext';
import Login               from './pages/Login';
import SetPassword         from './pages/SetPassword';
import Home               from './pages/Home';
import SmartBuyer         from './pages/SmartBuyer';
import CustomerOrdersNoStock from './pages/CustomerOrdersNoStock';
import ReceivedPOs        from './pages/ReceivedPOs';
import SmartBuyerDispatch from './pages/SmartBuyerDispatch';
import GeneralStockDispatch from './pages/GeneralStockDispatch';
import ReplicationStatus  from './pages/ReplicationStatus';
import AutomationEvents   from './pages/AutomationEvents';
import OpenPOTracker      from './pages/OpenPOTracker';
import BlockedOrders      from './pages/BlockedOrders';
import BlockedOrdersHistory from './pages/BlockedOrdersHistory';
import BlockedBySupplier  from './pages/BlockedBySupplier';
import BackOrderManagement from './pages/BackOrderManagement';
import BackOrderHistory   from './pages/BackOrderHistory';
import BackOrderFollowUpSchedule from './pages/BackOrderFollowUpSchedule';
import FollowUpSchedule   from './pages/FollowUpSchedule';
import SupplierSpend      from './pages/SupplierSpend';
import SupplierPurchaseOrders from './pages/SupplierPurchaseOrders';
import MarginWatch        from './pages/MarginWatch';
import ProductMarginWatch from './pages/ProductMarginWatch';
import ProductMarginWatchDetail from './pages/ProductMarginWatchDetail';
import ControlPanel       from './pages/ControlPanel';
import SupplierAutomation from './pages/SupplierAutomation';
import NextActions        from './pages/NextActions';
import CustomerBlacklist  from './pages/CustomerBlacklist';
import BrandExclusion     from './pages/BrandExclusion';
import Utilities          from './pages/Utilities';
import Reports            from './pages/Reports';
import DispatchTools      from './pages/DispatchTools';
import PoOpenOrders       from './pages/PoOpenOrders';
import OpenOrdersList     from './pages/OpenOrdersList';
import DispatchTracking   from './pages/DispatchTracking';
import FrontlineTools     from './pages/FrontlineTools';
import POMatchSearch      from './pages/POMatchSearch';
import HotBackOrders      from './pages/HotBackOrders';
import ProductErrorManagement from './pages/ProductErrorManagement';
import MarginCheck from './pages/MarginCheck';
import DigitalMetrics   from './pages/DigitalMetrics';
import DataSweep        from './pages/DataSweep';
import KnowledgeNuggets from './pages/KnowledgeNuggets';
import BenchClips       from './pages/BenchClips';
import Invoices         from './pages/Invoices';
import PoLanding        from './pages/PoLanding';
import TodoList           from './pages/TodoList';
import Help               from './pages/Help';
import HelpTopic          from './pages/HelpTopic';
import DispatchDrilldown  from './components/DispatchDrilldown';

const APP_VERSION = '4.5';

const AppHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, username } = useAuth();
  const isHome   = location.pathname === '/';
  return (
    <header className="app-header">
      <div className="app-header__inner">
        <div className="app-header__brand" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <span className="app-header__hc">HOBBY CITY</span>
          <span className="app-header__admin">ADMIN</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {username && <span className="app-header__user">{username}</span>}
          {!isHome && <button className="app-header__btn" onClick={() => navigate('/')}>⌂ Home</button>}
          {location.pathname !== '/supplier-pos' && (
            <button className="app-header__btn" onClick={() => navigate('/supplier-pos')}>📦 Supplier POs</button>
          )}
          {!location.pathname.startsWith('/help') && (
            <button className="app-header__btn" onClick={() => navigate('/help')}>📖 Help</button>
          )}
          <button className="app-header__btn" onClick={() => navigate('/set-password')}>Change Password</button>
          <button className="app-header__btn" onClick={signOut}>Sign Out</button>
        </div>
      </div>
    </header>
  );
};

function AppShell() {
  const { session, loading } = useAuth();
  const location = useLocation();

  if (location.pathname === '/set-password') {
    return <SetPassword />;
  }

  if (loading) {
    return <div className="app-loading">Loading…</div>;
  }

  if (!session) {
    return <Login />;
  }

  return (
    <div className="app">
      <AppHeader />
      <Routes>
        <Route path="/"                     element={<Home />} />
        <Route path="/smart-buyer"          element={<SmartBuyer />} />
        <Route path="/customer-orders-no-stock" element={<CustomerOrdersNoStock />} />
        <Route path="/received-pos"         element={<ReceivedPOs />} />
        <Route path="/smart-buyer-dispatch" element={<SmartBuyerDispatch />} />
        <Route path="/general-stock-dispatch" element={<GeneralStockDispatch />} />
        <Route path="/replication-status"   element={<ReplicationStatus />} />
        <Route path="/automation-events"    element={<AutomationEvents />} />
        <Route path="/dispatch-drilldown"   element={<DispatchDrilldown />} />
        <Route path="/open-po-tracker" element={<OpenPOTracker />} />
        <Route path="/blocked-orders"  element={<BlockedOrders />} />
        <Route path="/blocked-orders-history" element={<BlockedOrdersHistory />} />
        <Route path="/blocked-by-supplier" element={<BlockedBySupplier />} />
        <Route path="/backorder-management" element={<BackOrderManagement />} />
        <Route path="/backorder-history" element={<BackOrderHistory />} />
        <Route path="/backorder-follow-up-schedule" element={<BackOrderFollowUpSchedule />} />
        <Route path="/follow-up-schedule" element={<FollowUpSchedule />} />
        <Route path="/supplier-spend" element={<SupplierSpend />} />
        <Route path="/supplier-pos"   element={<SupplierPurchaseOrders />} />
        <Route path="/margin-watch"   element={<MarginWatch />} />
        <Route path="/product-margin-watch" element={<ProductMarginWatch />} />
        <Route path="/product-margin-items" element={<ProductMarginWatchDetail />} />
        <Route path="/control-panel"  element={<ControlPanel />} />
        <Route path="/control-panel/supplier-automation" element={<SupplierAutomation />} />
        <Route path="/control-panel/next-actions" element={<NextActions />} />
        <Route path="/control-panel/blacklist" element={<CustomerBlacklist />} />
        <Route path="/control-panel/brand-exclusion" element={<BrandExclusion />} />
        <Route path="/utilities" element={<Utilities />} />
        <Route path="/reports"   element={<Reports />} />
        <Route path="/dispatch-tools" element={<DispatchTools />} />
        <Route path="/po-open-orders" element={<PoOpenOrders />} />
        <Route path="/open-orders-list" element={<OpenOrdersList />} />
        <Route path="/dispatch-tracking" element={<DispatchTracking />} />
        <Route path="/starshipit-tracking" element={<Navigate to="/dispatch-tracking" replace />} />
        <Route path="/frontline-tools" element={<FrontlineTools />} />
        <Route path="/hot-back-orders" element={<HotBackOrders />} />
        <Route path="/utilities/po-match-search" element={<POMatchSearch />} />
        <Route path="/product-error-management" element={<ProductErrorManagement />} />
        <Route path="/margin-check" element={<MarginCheck />} />
        <Route path="/digital-metrics" element={<DigitalMetrics />} />
        <Route path="/data-sweep" element={<DataSweep />} />
        <Route path="/knowledge-nuggets" element={<KnowledgeNuggets />} />
        <Route path="/bench-clips" element={<BenchClips />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/po-landing" element={<PoLanding />} />
        <Route path="/todo-list" element={<TodoList />} />
        <Route path="/help" element={<Help />} />
        <Route path="/help/:slug" element={<HelpTopic />} />
      </Routes>
      <footer className="app-footer">
        Hobby City Admin · Powered by Supabase · v{APP_VERSION}
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </AuthProvider>
  );
}
